import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { finalize, map } from 'rxjs/operators';

import { EmailFormComponent, EmailFormModel } from 'entity/forms/email-form/email-form.component';
import { isNilOrEmpty } from 'helpers';

import {
  CreateEmailGQL, CreateEmailsInput, EmailType, FieldStatusInput, UpdateEmailGQL, UpdateEmailInput
} from 'generated/graphql';
import { invalidateDuplicatesCache } from 'entity/helpers/invalidate-apollo-duplicates-cache';

/**
 * The AddOrUpdateEmail dialog needs to have this data passed in.
 * if no email property is given, it will be assumed that the operation
 * is a create.
 */
export interface AddOrUpdateEmailDialogData {
  campaignId: string;
  entityId: string;
  email?: {
    id: string;
    status: any;
    email: string;
    emailType: any;
    subscribed: boolean;
  };
}

@Component({
  selector: 'app-add-or-update-email-dialog',
  templateUrl: './add-or-update-email.dialog.html',
})
export class AddOrUpdateEmailDialog implements OnInit {
  isSaving = false;
  emailControl: UntypedFormControl;

  static open(dialog: MatDialog, config: MatDialogConfig<AddOrUpdateEmailDialogData>) {
    return dialog.open(AddOrUpdateEmailDialog, config);
  }

  get isCreate() {
    return isNilOrEmpty(this.data?.email);
  }

  constructor(
    private _createEmailGQL: CreateEmailGQL,
    private _updateEmailGQL: UpdateEmailGQL,
    public dialogRef: MatDialogRef<AddOrUpdateEmailDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AddOrUpdateEmailDialogData) {
    if (isNilOrEmpty(data?.entityId) || isNilOrEmpty(data?.campaignId)) {
      throw new Error('An entityId and campaignId must be passed as data to `AddOrUpdateEmailDialog`');
    }
  }

  ngOnInit() {
    this.emailControl = new UntypedFormControl(
      EmailFormComponent.toFormModel(this.data.email)
    );
  }

  close(data: any = null) {
    this.dialogRef.close(data);
  }

  onSaveClicked() {
    if (this.isSaving) {
      return;
    }

    this._save$().pipe(
      finalize(() => this.isSaving = false)
    ).subscribe(
      (res) => {
        this.close(res);
      }
    );
  }

  private _save$() {
    this.isSaving = true;
    return this.isCreate ? this._saveEmail() : this._updateEmail();
  }

  _saveEmail() {
    const formValue = this.emailControl.value as EmailFormModel;

    const input: CreateEmailsInput = {
      entityId: this.data.entityId,
      campaignId: this.data.campaignId,
      emails: [
        {
          email: formValue.email,
          status: formValue.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
          emailType: formValue.emailType as EmailType,
          subscribe: formValue.subscribed
        }
      ]
    };

    return this._createEmailGQL.mutate(
      { input },
      {
        update: (cache) => invalidateDuplicatesCache(cache, this.data.entityId, this.data.campaignId)
      }
    ).pipe(
      map((response) => response?.data?.createEmails.entity)
    );
  }

  _updateEmail() {
    const formValue = this.emailControl.value as EmailFormModel;

    const input: UpdateEmailInput = {
      entityId: this.data.entityId,
      campaignId: this.data.campaignId,
      emailId: this.data.email.id,
      changes: {
        email: formValue.email,
        emailType: formValue.emailType as EmailType,
        status: formValue.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
        subscribe: formValue.subscribed
      }
    };

    return this._updateEmailGQL.mutate(
      { input },
      {
        update: (cache) => invalidateDuplicatesCache(cache, this.data.entityId, this.data.campaignId)
      }
    ).pipe(
      map((response) => response?.data?.updateEmail.entity)
    );
  }
}
